import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessageCircleIcon, CreditCardIcon, RepeatIcon, ChevronLeftIcon } from 'lucide-react';
import { useVehicles } from '../contexts/VehiclesContext';
import { onImageError } from '../utils/drive';
import { detailWhatsappMessage, formatKm, formatPrice, similarVehicles, vehicleTitle, whatsappLink } from '../utils/vehicles';
import { useSeo } from '../utils/seo';
import { Badge } from '../components/ui/Badge';
import { ButtonAnchor, ButtonLink } from '../components/ui/Button';
import { VehicleCard } from '../components/VehicleCard';
import { ConsultaForm } from '../components/forms/ConsultaForm';
export function VehicleDetail() {
  const {
    slug
  } = useParams();
  const {
    vehicles,
    loading
  } = useVehicles();
  const vehicle = vehicles.find(v => v.slug === slug);
  const [activeImage, setActiveImage] = useState(0);
  useSeo(vehicle ? `${vehicleTitle(vehicle)} ${vehicle.anio} | VCARS` : 'Vehículo no encontrado | VCARS', vehicle?.descripcionCorta);
  if (!vehicle && loading) {
    return <main className="mx-auto w-full max-w-7xl px-5 py-16 md:px-8" data-id="element-770" data-mp="2hf49t4g9i9t2">
        <div className="h-[420px] animate-pulse rounded-2xl border border-line bg-surface2/40" data-id="element-771" data-mp="2ve42qoat5av1" />
      </main>;
  }
  if (!vehicle) {
    return <main className="mx-auto w-full max-w-3xl px-5 py-24 text-center md:px-8" data-id="element-772" data-mp="2qfkc4al9rawc">
        <h1 className="text-3xl font-extrabold text-white" data-id="element-773" data-mp="22qhxlblxly62">
          No encontramos ese vehículo
        </h1>
        <p className="mt-3 text-mist" data-id="element-774" data-mp="1xrog783ixn0m">
          Puede que la unidad ya se haya vendido. Mirá el catálogo completo o dejanos tu
          consulta.
        </p>
        <ButtonLink to="/catalogo" size="lg" className="mt-7" data-id="element-775" data-mp="27ot69j0hq2mp">
          Ver catálogo
        </ButtonLink>
      </main>;
  }
  const gallery = vehicle.galeria.length ? vehicle.galeria : [vehicle.imagenPrincipal];
  const title = vehicleTitle(vehicle);
  const specs: [string, string][] = [['Año', String(vehicle.anio)], ['Kilometraje', formatKm(vehicle.kilometraje)], ['Estado', vehicle.estado === '0KM' ? '0KM' : 'Usado seleccionado'], ['Tipo', vehicle.tipo], ['Combustible', vehicle.combustible], ['Transmisión', vehicle.transmision], ['Color', vehicle.color], ['Ubicación', vehicle.ubicacion], ['Publicación', new Date(vehicle.fechaPublicacion).toLocaleDateString('es-AR')], ['Código', vehicle.id]];
  return <main className="bg-ink" data-id="element-776" data-mp="2w53rfea0i06u">
      <div className="mx-auto w-full max-w-7xl px-5 pt-8 md:px-8" data-id="element-777" data-mp="312c97dr9027k">
        <Link to="/catalogo" className="inline-flex items-center gap-1.5 text-sm font-semibold text-mist transition-colors duration-150 ease-smooth hover:text-white" data-id="element-778" data-mp="312m156vusjfw">
          <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-779" data-mp="3a4bvoll17yhw" />
          Volver al catálogo
        </Link>
      </div>

      <div className="mx-auto grid w-full max-w-7xl gap-10 px-5 py-8 md:px-8 lg:grid-cols-[1.35fr_1fr] lg:gap-14" data-id="element-780" data-mp="pct8mou2hkf3">
        <div data-id="element-781" data-mp="zggor1709ca9">
          <div className="overflow-hidden rounded-2xl border border-line bg-surface2" data-id="element-782" data-mp="zgzvdlr7ge8z">
            <img src={gallery[activeImage]} alt={`${title} ${vehicle.anio} — imagen ${activeImage + 1}`} onError={onImageError} referrerPolicy="no-referrer" className="aspect-[4/3] w-full object-cover" data-id="element-783" data-mp="uh55nlj61upi" />
          </div>
          {gallery.length > 1 ? <div className="mt-3 flex gap-3" role="group" aria-label="Galería de imágenes" data-id="element-784" data-mp="grtlmo8e2wzb">
              {gallery.map((img, i) => <button key={img + i} type="button" onClick={() => setActiveImage(i)} aria-label={`Ver imagen ${i + 1}`} aria-current={i === activeImage} className={`h-20 w-28 overflow-hidden rounded-xl border transition-colors duration-150 ease-smooth ${i === activeImage ? 'border-neon' : 'border-line hover:border-neon/60'}`} data-id="element-785" data-mp="btiama6cygfi" data-mpi={i}>
                  <img src={img} alt="" onError={onImageError} referrerPolicy="no-referrer" className="h-full w-full object-cover" data-id="element-786" data-mp="2971gsr5885xy" />
                </button>)}
            </div> : null}

          <section className="mt-10" data-id="element-787" data-mp="1lfmfn1p6ruoa">
            <h2 className="text-xl font-bold text-white" data-id="element-788" data-mp="1q7xdlz0d96e7">Descripción</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-mist" data-id="element-789" data-mp="1qa0cz9nesbvl">
              {vehicle.descripcionCompleta}
            </p>
          </section>

          <section className="mt-10" data-id="element-790" data-mp="1ghci4kfzohzi">
            <h2 className="text-xl font-bold text-white" data-id="element-791" data-mp="1gidtyr4tx8ia">Información técnica</h2>
            <dl className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-3" data-id="element-792" data-mp="12jnuj4ujewqr">
              {specs.map(([k, v]) => <div key={k} className="bg-surface2/60 px-4 py-3.5" data-id="element-793" data-mp="nv4d7xkb97ki">
                  <dt className="text-[12px] font-semibold uppercase tracking-wide text-mist" data-id="element-794" data-mp="nxh1esgnwe60">
                    {k}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-white" data-id="element-795" data-mp="wwkkvxjqqvl1">{v}</dd>
                </div>)}
            </dl>
          </section>
        </div>

        <div className="lg:sticky lg:top-24 lg:self-start" data-id="element-796" data-mp="16ui9wn9fcwep">
          <div className="flex flex-wrap gap-2" data-id="element-797" data-mp="16sxcmpvq6zd5">
            {vehicle.destacado ? <Badge tone="neon" data-id="element-798" data-mp="11uds8tws5vmw">Destacado</Badge> : null}
            {vehicle.oportunidad ? <Badge tone="amber" data-id="element-799" data-mp="1gktemc0xn9mn">Oportunidad</Badge> : null}
            {vehicle.financiacion ? <Badge tone="green" data-id="element-800" data-mp="1bl8dpw27cr7a">Financiación</Badge> : null}
            {vehicle.permuta ? <Badge data-id="element-801" data-mp="1bnbdv8x2k0pq">Permuta</Badge> : null}
          </div>
          <h1 className="mt-4 text-3xl font-black leading-tight tracking-tight text-white md:text-[40px]" data-id="element-802" data-mp="1pm1dav7d2ch9">
            {vehicle.marca} {vehicle.modelo}
          </h1>
          <p className="mt-2 text-base text-mist" data-id="element-803" data-mp="i8qv44b2e6n5">
            {vehicle.version} · {vehicle.anio} · {formatKm(vehicle.kilometraje)}
          </p>
          <p className="mt-6 text-[34px] font-black tracking-tight text-white md:text-[44px]" data-id="element-804" data-mp="n8d9ih2mpsxj">
            {formatPrice(vehicle.precio, vehicle.moneda)}
          </p>
          <p className="mt-1 text-sm text-mist" data-id="element-805" data-mp="w0f787lbcv44">
            {vehicle.ubicacion} · Vendedor: {vehicle.vendedor}
          </p>

          <div className="mt-7 flex flex-col gap-2.5" data-id="element-806" data-mp="10y6vmrmr1z3k">
            <ButtonAnchor href={whatsappLink(detailWhatsappMessage(vehicle), vehicle.whatsapp)} variant="whatsapp" size="lg" data-id="element-807" data-mp="15xta14ebdldy">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-808" data-mp="1avbm33nmogq6" />
              Consultar por WhatsApp
            </ButtonAnchor>
            <ButtonLink to={`${vehicle.estado === '0KM' ? '/financiacion' : '/financiar-usado'}?vehiculo=${encodeURIComponent(`${title} ${vehicle.anio}`)}`} size="lg" data-id="element-809" data-mp="3cpavfjh6wo08">
              <CreditCardIcon className="h-4 w-4" aria-hidden="true" data-id="element-810" data-mp="397wncuqm72oy" />
              Quiero financiar este vehículo
            </ButtonLink>
            <ButtonLink to={`/permutas?vehiculo=${encodeURIComponent(`${title} ${vehicle.anio}`)}`} variant="outline" size="lg" data-id="element-811" data-mp="2zgiqyvtymj01">
              <RepeatIcon className="h-4 w-4" aria-hidden="true" data-id="element-812" data-mp="3s5hbfzlk1x8i" />
              Tengo una permuta
            </ButtonLink>
          </div>

          <p className="mt-5 text-[13px] leading-relaxed text-mist" data-id="element-813" data-mp="3n7ftxj3s25z5">
            Te acompañamos durante toda la operación: verificación, documentación y
            transferencia.
          </p>

          <div className="mt-8" data-id="element-814" data-mp="3e6hm0pq7xeba">
            <ConsultaForm title="Consulta rápida" lead="Dejanos tus datos y un asesor te contacta por esta unidad." motivoInicial="Quiero comprar un auto" vehiculoInicial={`${title} ${vehicle.anio}`} data-id="element-815" data-mp="3e5ey8qo6zrzl" />
          </div>
        </div>
      </div>

      <section className="border-t border-line/60 py-16" data-id="element-816" data-mp="6phhs7zof1d7">
        <div className="mx-auto w-full max-w-7xl px-5 md:px-8" data-id="element-817" data-mp="aq1dd74lpwx8">
          <h2 className="text-2xl font-extrabold tracking-tight text-white md:text-3xl" data-id="element-818" data-mp="3sul7xw78ymtn">
            Vehículos similares
          </h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-819" data-mp="1pcyybbapujt">
            {similarVehicles(vehicles, vehicle).map(v => <VehicleCard key={v.id} vehicle={v} data-id="element-820" data-mp="pfz702lavett" />)}
          </div>
        </div>
      </section>
    </main>;
}